import { Suspense } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  Building2,
  ExternalLink,
  LayoutDashboard,
  LogOut,
  Package,
  Receipt,
  Shield,
  ShoppingBag,
  Truck,
  Users,
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { ChargementPage } from '@/components/layout/lazyPage';

type NavGroup = {
  title: string;
  icon: typeof Shield;
  /** Seuls les comptes de ces rôles voient le groupe. */
  platformOnly: boolean;
  items: Array<{ to: string; label: string; icon: typeof Shield; end: boolean }>;
};

/**
 * Deux groupes, deux périmètres.
 *
 * La plateforme et la boutique n'ont ni les mêmes données ni les mêmes
 * responsables : les mélanger dans une liste plate laissait croire qu'un
 * catalogue et un club relèvent du même travail.
 */
const NAV_GROUPS: NavGroup[] = [
  {
    title: 'Plateforme',
    icon: Shield,
    platformOnly: true,
    items: [
      { to: '/admin', label: "Vue d'ensemble", icon: LayoutDashboard, end: true },
      { to: '/admin/utilisateurs', label: 'Utilisateurs', icon: Users, end: false },
      { to: '/admin/clubs', label: 'Clubs', icon: Building2, end: false },
    ],
  },
  {
    title: 'Boutique',
    icon: ShoppingBag,
    platformOnly: false,
    items: [
      { to: '/admin/boutique', label: 'Tableau de bord', icon: LayoutDashboard, end: true },
      { to: '/admin/boutique/produits', label: 'Catalogue', icon: Package, end: false },
      { to: '/admin/boutique/commandes', label: 'Commandes', icon: Receipt, end: false },
      { to: '/admin/boutique/livraison', label: 'Livraison', icon: Truck, end: false },
    ],
  },
];

/**
 * Cadre des espaces d'administration.
 *
 * Hors de MainLayout : la navigation du site (fil, marketplace, clubs) n'a rien
 * à faire ici, et l'admin doit voir immédiatement qu'il n'est plus dans
 * l'application publique.
 */
export default function AdminLayout() {
  const navigate = useNavigate();
  const { user, clearAuth } = useAuthStore();

  const isPlatformAdmin = user?.user_type === 'admin';
  const groups = NAV_GROUPS.filter((group) => isPlatformAdmin || !group.platformOnly);

  const handleLogout = () => {
    clearAuth();
    navigate('/admin/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col md:flex-row">
      <aside className="md:w-64 bg-slate-900 text-slate-300 md:min-h-screen flex md:flex-col">
        <div className="p-5 border-b border-slate-800 hidden md:block">
          <img src="/brand/fconnect-logo-horizontal-blanc.svg" alt="FCONNECT" className="h-9 w-auto" />
          <p className="text-xs text-slate-500 mt-1">
            {isPlatformAdmin ? 'Administration' : 'Administration boutique'}
          </p>
        </div>

        <nav className="flex md:flex-col gap-1 p-2 md:p-3 flex-1 overflow-x-auto">
          {groups.map((group) => (
            <div key={group.title} className="flex md:flex-col gap-1 md:mb-3">
              {/* Un seul groupe visible : son intitulé n'apprendrait rien. */}
              {groups.length > 1 && (
                <p className="hidden md:flex items-center gap-1.5 px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  <group.icon className="h-3 w-3" />
                  {group.title}
                </p>
              )}
              {group.items.map(({ to, label, icon: Icon, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3 py-2 rounded-lg text-sm whitespace-nowrap transition ${
                      isActive ? 'bg-slate-800 text-white' : 'hover:bg-slate-800/60'
                    }`
                  }
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="p-3 border-t border-slate-800 hidden md:block">
          <p className="text-xs text-slate-500 truncate" title={user?.email}>
            {user?.email}
          </p>
          <p className="text-[10px] text-slate-600 mt-0.5">
            {isPlatformAdmin ? 'Admin plateforme' : 'Admin boutique'}
          </p>
          <a
            href="/marketplace"
            className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-slate-800/60"
          >
            <ExternalLink className="h-4 w-4" />
            Voir la boutique
          </a>
          <button
            type="button"
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-slate-800/60 text-left"
          >
            <LogOut className="h-4 w-4" />
            Se déconnecter
          </button>
        </div>

        <button
          type="button"
          onClick={handleLogout}
          className="md:hidden px-3 text-sm hover:text-white"
          aria-label="Se déconnecter"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </aside>

      <main className="flex-1 min-w-0">
        <Suspense fallback={<ChargementPage />}>
          <Outlet />
        </Suspense>
      </main>
    </div>
  );
}
